/**
 * The catalogue raisonné as the estate reads it: every work registered as
 * being by its artist, searchable, and split by whether this estate has
 * confirmed that it is.
 *
 * MOST OF THE LIST IS NOT THE CATALOGUE, and since OND-20 the screen says so.
 * `ondo.estate_catalogue()` returns every work anybody has NAMED this artist
 * on — it always did — and a catalogue number is now issued by a confirmed
 * authorship claim and by nothing else. So a row is either confirmed (the
 * catalogue proper), a preserved legacy row carrying a number nobody has
 * confirmed, or a work somebody named this artist on. Each is labelled as what
 * it is; none of the three is called a verdict.
 *
 * Pure. One response's rows go in; the header, the banner, the chip counts
 * and the filtered rows come out. The copy is `estate.constants.ts`; the read,
 * the two-press declaration and the search and chip state are
 * `useCatalogueController.ts`. The chips and the search narrow the rows the
 * header counted, never a second request that could disagree with it.
 *
 * The declare control appears only for one undeclared catalogue and an admin
 * seat; anything else leaves the banner without a button the server would
 * refuse. A work numbered before the number space existed shows N/A, not a
 * number invented for it.
 */

import type { CatalogueMeta, CatalogueWork } from "@ondo/shared";

import { CATALOGUE_COPY } from "./estate.constants.ts";

/**
 * The catalogue raisonné, as the screen shows it.
 *
 * Everything here is derived from the one response `GET /v1/estate/catalogue`
 * returns — counts over the rows that arrived, never a second request that
 * could disagree. What a row may say is decided in SQL (20260911090000
 * decision 4): no owner, no placement, no sale state, so no chip here claims
 * one.
 */

/** The chip row. Derived states only — nothing stores a colour. */
export const CATALOGUE_CHIPS = ["all", "attested", "unattested", "edition"] as const;

export type CatalogueChip = (typeof CATALOGUE_CHIPS)[number];

export interface CatalogueRow {
  workId: string;
  title: string;
  /** '1997 · C-print · 70 × 57 cm · ed. 3/3'. Whatever exists, dot-joined. */
  meta: string;
  ondoCode: string;
  /**
   * 'CR-031', or the copy's NOT NUMBERED — and since OND-20 the second means
   * something stronger than missing data. See `crLabel`.
   */
  cr: string;
  /**
   * Said on a row that carries a number no confirmation stands behind.
   *
   * The preserved legacy rows, and the whole reason "confirmed works are the
   * catalogue" can be said of a list that still holds them. Null on every other
   * row — a numbered row WITH a confirmation needs no explanation, and an
   * unnumbered one is explained by its number column.
   */
  crNote: string | null;
  /**
   * What ANY authorised authority has said about who made this.
   *
   * The work's own fact, as against `attested` below, which is the caller's.
   * They answer different questions and a catalogue that printed one as the
   * other would be wrong in the direction that matters: a work another
   * authority confirmed is in the catalogue and is not confirmed by US.
   */
  attribution: string;
  /**
   * Whether an authority has confirmed the authorship — so whether this row is
   * in the catalogue proper at all, as against merely named.
   */
  confirmed: boolean;
  /** Whether THIS estate confirmed the AUTHORSHIP. Narrowed by OND-20. */
  attested: boolean;
  /**
   * WHO OWNS IT — OND-58. 'Michael Hue-Williams', or the copy's withheld line.
   *
   * Always a sentence, never null, because the row has to say something in both
   * states: a blank cell reads as missing data, and "the owner has chosen not
   * to be named" is a fact the estate is entitled to see. The decision of which
   * of the two to show is not made here — the server sent a name or it sent
   * null, and this picks the matching copy.
   */
  owner: string;
  /**
   * Whether the owner has opened the whole record to this estate.
   *
   * Separate from `owner`, and that separation is the product rule: an owner
   * may be named without granting anything, and may grant without being named.
   * A single "access" label covering both would make the two look like one
   * setting, which is the thing the ticket is careful to deny.
   */
  recordOpen: boolean;
  imagePath: string | null;
  href: string;
}

export type CatalogueView =
  | { kind: "loading"; message: string }
  | { kind: "error"; title: string; message: string }
  | { kind: "empty"; lead: string; detail: string }
  | {
      kind: "open";
      /** 'Erwin Wurm — 14 works · 11 attested by this estate'. */
      header: string;
      /**
       * The in-progress banner with its one action, or the declared line, or
       * nothing — three states the union keeps apart so "declared and also
       * declarable" cannot render.
       */
      banner:
        | { state: "in-progress"; declare: { artistId: string; label: string } | null }
        | { state: "declared"; line: string };
      counts: Record<CatalogueChip, number>;
      rows: CatalogueRow[];
      /** 'x of y works', under the search box. */
      showing: string;
    };

export function toCatalogueView(input: {
  catalogues: CatalogueMeta[];
  works: CatalogueWork[];
  query: string;
  chip: CatalogueChip;
  /** Admin on this workspace — `atLeast(membership, 'admin')`, true when personal. */
  canDeclare: boolean;
  workBase: string;
}): Extract<CatalogueView, { kind: "open" | "empty" }> {
  const { catalogues, works } = input;

  if (works.length === 0) {
    return { kind: "empty", lead: CATALOGUE_COPY.emptyLead, detail: CATALOGUE_COPY.emptyDetail };
  }

  const attested = works.filter((work) => work.attested).length;
  const counts: Record<CatalogueChip, number> = {
    all: works.length,
    attested,
    unattested: works.length - attested,
    edition: works.filter((work) => work.editionSize !== null).length,
  };

  const shown = works
    .filter((work) => matchesChip(work, input.chip))
    .filter((work) => matchesQuery(work, input.query))
    .map((work) => toRow(work, input.workBase));

  // The mock's single-catalogue header, kept honest for zero and many: an
  // estate attributed works before any identity link has works and no
  // catalogue meta, and the header then counts what it can see.
  const name =
    catalogues.length > 0
      ? catalogues.map((c) => c.artistName).join(" · ")
      : (works[0]?.attributedName ?? "");
  const header = CATALOGUE_COPY.header(name, works.length, attested);

  const declared = catalogues.find((c) => c.declaredCompleteAt !== null) ?? null;
  const undeclared = catalogues.filter((c) => c.declaredCompleteAt === null);

  return {
    kind: "open",
    header,
    banner: declared
      ? { state: "declared", line: CATALOGUE_COPY.declaredLine(declared.declaredCompleteAt ?? "") }
      : {
          state: "in-progress",
          // One catalogue, admin seat: the button. Anything else — several
          // catalogues, a junior seat, no identity link yet — the banner
          // stands without a control the server would refuse.
          declare:
            input.canDeclare && undeclared.length === 1 && undeclared[0]
              ? { artistId: undeclared[0].artistId, label: CATALOGUE_COPY.declare }
              : null,
        },
    counts,
    rows: shown,
    showing: CATALOGUE_COPY.showing(shown.length, works.length),
  };
}

/**
 * 'CR-031'. Three digits because a catalogue in the hundreds is the norm.
 *
 * NULL IS NOT MISSING DATA SINCE OND-20. `crSeq` was null only for a work
 * attributed before the number space existed; since
 * `20261004096000_artist_attribution.sql` a number is issued by a confirmed
 * authorship claim and by nothing else, so null now says nobody with authority
 * has confirmed that this work is by this artist. `CATALOGUE_COPY.noNumber`
 * carries the change in words — the key is unchanged on purpose, because the
 * browser suite points at it.
 */
export function crLabel(seq: number | null): string {
  if (seq === null) return CATALOGUE_COPY.noNumber;

  return `CR-${String(seq).padStart(3, "0")}`;
}

/**
 * HOW A ROW SAYS WHETHER ANYBODY WITH AUTHORITY AGREES — OND-20.
 *
 * `null` state is the ordinary case and has its own sentence rather than
 * borrowing `not_requested`'s: a work somebody merely named this artist on
 * carries no authorship claim at all, so there is nothing for anybody to have
 * answered. `not_requested` means the claim IS on the record and nobody has
 * answered it, which is a step further along.
 *
 * Neither reads as a rejection, and nor does `awaiting`: silence is neither
 * acceptance nor rejection, and nothing in this flow expires.
 */
export function describeRowAttribution(state: CatalogueWork["attributionState"]): string {
  return state === null ? CATALOGUE_COPY.rowNoClaim : CATALOGUE_COPY.rowStates[state];
}

/**
 * Whether this row belongs to the catalogue PROPER rather than merely to the
 * list of works somebody named this artist on.
 *
 * Read off the state and NOT off `crSeq`, which is the distinction the legacy
 * rows exist to break: a preserved row carries a number and no confirmation,
 * and deriving confirmation from the number would silently re-authenticate
 * every one of them — the exact backfill 20261004096000 declined to write.
 *
 * `confirmed` ALONE, AND NOT `attributionAnswered` — OND-215. A confirmation
 * from somebody ONDO never appointed over the artist is shown on the row (see
 * `rowStates`) and is NOT a catalogue entry, because a number is issued by an
 * appointed authority's confirmation and by nothing else:
 * `ondo.mint_catalogue_entry_on_attribution()` still requires a live
 * appointment, so a row admitted here would carry no number and claim one.
 * OND-54's forged Fat Car at row 42 of the Wurm catalogue is the reason that
 * trigger is narrow, and widening this predicate would reopen it from the
 * reading side.
 */
export function isConfirmed(work: CatalogueWork): boolean {
  return work.attributionState === "confirmed";
}

export function matchesChip(work: CatalogueWork, chip: CatalogueChip): boolean {
  if (chip === "all") return true;
  if (chip === "attested") return work.attested;
  if (chip === "unattested") return !work.attested;

  return work.editionSize !== null;
}

/**
 * The search the mock's placeholder promises: CR number, year, title or ONDO
 * id — matched case-blind on what the row actually shows, so 'cr-003' and
 * '003' both find CR-003.
 */
export function matchesQuery(work: CatalogueWork, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;

  return [work.title, work.year ?? "", work.ondoCode, crLabel(work.crSeq)]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

function toRow(work: CatalogueWork, workBase: string): CatalogueRow {
  const meta = [
    work.year,
    work.medium,
    work.dimensions,
    work.editionText ? `ed. ${work.editionText}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const confirmed = isConfirmed(work);

  return {
    workId: work.workId,
    title: work.title,
    meta,
    ondoCode: work.ondoCode,
    cr: crLabel(work.crSeq),
    // A number with no confirmation behind it is a preserved legacy row and
    // says so. The two halves are deliberately not derived from one another —
    // see `isConfirmed`.
    crNote: work.crSeq !== null && !confirmed ? CATALOGUE_COPY.legacyNumber : null,
    attribution: describeRowAttribution(work.attributionState),
    confirmed,
    attested: work.attested,
    // OND-58. The name, or the sentence that says there is one and it is
    // withheld. No `?? ""`: an empty cell is indistinguishable from a column
    // that failed to load, and the withheld case is a real answer.
    owner: work.ownerNameToEstate ?? CATALOGUE_COPY.ownerWithheld,
    recordOpen: work.recordAccessGranted,
    imagePath: work.imagePath,
    href: `${workBase}/${work.workId}`,
  };
}
